/**
 * Scripting Feature - Public API
 *
 * This module provides engine-agnostic SQL script generation.
 * Use getScriptProvider(engine) to get the appropriate provider for
 * generating table and database scripts.
 *
 * Example usage:
 * ```typescript
 * import { getScriptProvider } from '@/features/scripting';
 *
 * const provider = getScriptProvider(connection.engine);
 * const script = provider.generateTableScript('select-top', {
 *   database: 'MyDb',
 *   schema: 'dbo',
 *   tableName: 'Users',
 * });
 * ```
 */

// Types
export type {
  DatabaseEngine,
  TableScriptType,
  DatabaseScriptType,
  SchemaScriptType,
  SchemaScriptContext,
  ColumnMetadata,
  ConstraintMetadata,
  IndexMetadata,
  TableMetadata,
  TableScriptContext,
  DatabaseMetadata,
  DatabaseScriptContext,
  NewObjectTemplateContext,
  ScriptProvider,
} from './types/scriptProvider';

// Registry
export {
  registerScriptProvider,
  getScriptProvider,
  hasScriptProvider,
  getRegisteredEngines,
} from './registry/scriptProviderRegistry';

// Providers (exported for direct access if needed)
export { mssqlScriptProvider } from './providers/mssqlScriptProvider';
export { pgsqlScriptProvider } from './providers/pgsqlScriptProvider';
export { mysqlScriptProvider } from './providers/mysqlScriptProvider';
export { sqliteScriptProvider } from './providers/sqliteScriptProvider';
export { oracleScriptProvider } from './providers/oracleScriptProvider';
export { kustoScriptProvider } from './providers/kustoScriptProvider';

// Auto-register providers on import
import { registerScriptProvider } from './registry/scriptProviderRegistry';
import { mssqlScriptProvider } from './providers/mssqlScriptProvider';
import { pgsqlScriptProvider } from './providers/pgsqlScriptProvider';
import { mysqlScriptProvider } from './providers/mysqlScriptProvider';
import { sqliteScriptProvider } from './providers/sqliteScriptProvider';
import { oracleScriptProvider } from './providers/oracleScriptProvider';
import { kustoScriptProvider } from './providers/kustoScriptProvider';

registerScriptProvider(mssqlScriptProvider);
registerScriptProvider(pgsqlScriptProvider);
registerScriptProvider(mysqlScriptProvider);
registerScriptProvider(sqliteScriptProvider);
registerScriptProvider(oracleScriptProvider);
registerScriptProvider(kustoScriptProvider);
